import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { agentService } from 'api/services/agentService';
import { corporateService } from 'api/services/corporateService';
import { otaService } from 'api/services/otaService';
import { FOLLOW_PARTY, SETTLEMENT_MODE_OPTIONS, settlementModeLabel, type SettlementChoice, type SettlementMode } from 'api/types/settlement.types';
import { BOOKING_SOURCE } from '../booking.utils';
import { useBookingSources } from 'hooks/api/useBookingSources';

type PartyKind = 'AGENT' | 'CORPORATE' | 'OTA';

/** What the settlement field needs to know about the booking's party. */
interface PartySettlement {
  name: string;
  defaultMode: SettlementMode;
  isCreditEligible: boolean;
}

/** The party's name, default settlement and credit eligibility, read from its master. */
const fetchPartySettlement = async (kind: PartyKind, id: string): Promise<PartySettlement | null> => {
  const res: any = kind === 'AGENT' ? await agentService.getById(id)
    : kind === 'CORPORATE' ? await corporateService.getById(id)
      : await otaService.getById(id);
  const rec = res?.data ?? res;
  if (!rec) return null;
  return {
    name: rec.agentName ?? rec.companyName ?? rec.otaName ?? '',
    defaultMode: (rec.defaultSettlementMode as SettlementMode) || 'GUEST',
    isCreditEligible: !!rec.isCreditEligible,
  };
};

// ============================================================================
// TYPES
// ============================================================================

export interface SourceData {
  bookingSource: string;
  agentId: string;
  agentName: string;
  corporateId: string;
  corporateName: string;
  otaId: string;
  otaName: string;
  otaBookingRef: string;
  /** Who pays (PMS slice 3b): PARTY = as the party master says (2026-10-16); GUEST when the booking has no party. */
  settlementMode: SettlementChoice;
}

interface SourceChannelSectionProps {
  data: SourceData;
  onChange: (updates: Partial<SourceData>) => void;
  fieldErrors: Record<string, string>;
}

// ============================================================================
// COMPONENT
// ============================================================================

const SourceChannelSection: React.FC<SourceChannelSectionProps> = ({
  data, onChange, fieldErrors,
}) => {
  // Enum-driven: the selectable list comes from common.tbl_enum_master, so a
  // new source needs no code change. No hardcoded fallback by design.
  const { options: sourceOptions, loading: sourcesLoading, error: sourcesError } = useBookingSources();

  // ---- Settlement (PMS slice 3b) -------------------------------------------
  // The booking's party, as the server reads it (PartyOf): the agent, corporate or
  // OTA that the source names. No party = the guest settles.
  const party: { kind: PartyKind; id: string } | null =
    data.bookingSource === BOOKING_SOURCE.TRAVEL_AGENT && data.agentId ? { kind: 'AGENT', id: data.agentId }
      : data.bookingSource === BOOKING_SOURCE.CORPORATE && data.corporateId ? { kind: 'CORPORATE', id: data.corporateId }
        : data.bookingSource === BOOKING_SOURCE.OTA && data.otaId ? { kind: 'OTA', id: data.otaId }
          : null;
  const partyKey = party ? `${party.kind}:${party.id}` : '';

  // Name / eligibility of the current party, for the field and its hint. Reading
  // it never changes the mode — an edited booking keeps what was saved.
  const [partyInfo, setPartyInfo] = useState<PartySettlement | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!party) { setPartyInfo(null); return; }
    fetchPartySettlement(party.kind, party.id)
      .then((info) => { if (!cancelled) setPartyInfo(info); })
      .catch(() => { if (!cancelled) setPartyInfo(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyKey]);

  // A party the USER picks: the stay follows that party's master (2026-10-16) —
  // the previous choice was about another party. Only user actions call this.
  const [resetNote, setResetNote] = useState<string | null>(null);
  const latestPick = useRef('');
  const applyPartyDefault = useCallback(async (kind: PartyKind | null, id: string) => {
    const key = kind && id ? `${kind}:${id}` : '';
    latestPick.current = key;
    if (!kind || !id) {
      if (data.settlementMode !== 'GUEST') setResetNote('Settlement reset to Guest pays — the booking has no agent, corporate or OTA.');
      onChange({ settlementMode: 'GUEST' });
      return;
    }
    try {
      const info = await fetchPartySettlement(kind, id);
      if (latestPick.current !== key || !info) return; // a later pick won
      onChange({ settlementMode: FOLLOW_PARTY });
      setResetNote(`Settlement follows ${info.name}'s master — now ${settlementModeLabel(info.defaultMode)}.`);
    } catch {
      /* the server applies the party default when the form sends none */
    }
  }, [data.settlementMode, onChange]);

  const handleSourceChange = useCallback((val: string | string[] | null) => {
    const source = (val as string) || BOOKING_SOURCE.DIRECT;
    const updates: Partial<SourceData> = { bookingSource: source };
    // Clear conditional fields when source changes
    if (source !== BOOKING_SOURCE.TRAVEL_AGENT) { updates.agentId = ''; updates.agentName = ''; }
    if (source !== BOOKING_SOURCE.CORPORATE) { updates.corporateId = ''; updates.corporateName = ''; }
    if (source !== BOOKING_SOURCE.OTA) { updates.otaId = ''; updates.otaName = ''; updates.otaBookingRef = ''; }
    onChange(updates);
    if (source !== data.bookingSource) void applyPartyDefault(null, '');
  }, [onChange, applyPartyDefault, data.bookingSource]);

  const settlementOptions = [
    {
      value: FOLLOW_PARTY as string,
      label: `As party master${partyInfo ? ` (now ${settlementModeLabel(partyInfo.defaultMode)})` : ''}`,
      isDisabled: false,
    },
    ...SETTLEMENT_MODE_OPTIONS.map((o) => ({
      value: o.value as string,
      label: o.credit && partyInfo && !partyInfo.isCreditEligible ? `${o.label} (party not credit eligible)` : o.label,
      isDisabled: o.credit && !!partyInfo && !partyInfo.isCreditEligible,
    })),
  ];
  const settlementHint = data.settlementMode === FOLLOW_PARTY
    ? "Each charge follows the party master's settlement when it posts — a change on the master reaches this stay."
    : SETTLEMENT_MODE_OPTIONS.find((o) => o.value === data.settlementMode)?.hint;

  const loadAgentOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await agentService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return res.data.map((a: any) => ({ value: a.id, label: `${a.agentCode} - ${a.agentName}` }));
  }, []);

  const loadCorporateOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await corporateService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return res.data.map((c: any) => ({ value: c.id, label: `${c.corporateCode} - ${c.companyName}` }));
  }, []);

  const loadOtaOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await otaService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return res.data.map((o: any) => ({ value: o.id, label: `${o.otaCode} - ${o.otaName}` }));
  }, []);

  const showAgent = data.bookingSource === BOOKING_SOURCE.TRAVEL_AGENT;
  const showCorporate = data.bookingSource === BOOKING_SOURCE.CORPORATE;
  const showOta = data.bookingSource === BOOKING_SOURCE.OTA;

  return (
    <div>
      <div className="booking-section-title">
        <IconSystem.link size={15} />
        Source & Channel
      </div>

      <div className="form-grid-2">
        <div style={showOta ? undefined : { gridColumn: '1 / -1' }}>
          <SyutSelect
            label="Booking Source"
            options={sourceOptions}
            value={data.bookingSource}
            onChange={(val) => handleSourceChange(val)}
            size="md"
            isDisabled={sourcesLoading}
            placeholder={sourcesLoading ? 'Loading sources...' : undefined}
            error={sourcesError ?? undefined}
            menuPortalTarget={document.body}
            // A saved booking may carry a source since retired from the enum;
            // seed it so the control never renders blank on an existing record.
            seedOptions={
              data.bookingSource && !sourceOptions.some((o) => o.value === data.bookingSource)
                ? [{ value: data.bookingSource, label: data.bookingSource }]
                : undefined
            }
          />
        </div>

        {showAgent && (
          <div style={{ gridColumn: '1 / -1' }}>
            <SyutSelect
              label={<>Travel Agent <span className="text-danger">*</span></>}
              isAsync
              loadOptions={loadAgentOptions}
              defaultOptions={true}
              cacheOptions={false}
              value={data.agentId}
              onChange={((val: string | string[] | null, _: any, opt?: SelectOption | null) => {
                onChange({ agentId: (val as string) || '', agentName: opt?.label || '' });
                void applyPartyDefault('AGENT', (val as string) || '');
              }) as any}
              placeholder="Select agent..."
              size="md"
              error={fieldErrors['agentId']}
              menuPortalTarget={document.body}
            />
          </div>
        )}

        {showCorporate && (
          <div style={{ gridColumn: '1 / -1' }}>
            <SyutSelect
              label={<>Corporate <span className="text-danger">*</span></>}
              isAsync
              loadOptions={loadCorporateOptions}
              defaultOptions={true}
              cacheOptions={false}
              value={data.corporateId}
              onChange={((val: string | string[] | null, _: any, opt?: SelectOption | null) => {
                onChange({ corporateId: (val as string) || '', corporateName: opt?.label || '' });
                void applyPartyDefault('CORPORATE', (val as string) || '');
              }) as any}
              placeholder="Select corporate..."
              size="md"
              error={fieldErrors['corporateId']}
              menuPortalTarget={document.body}
            />
          </div>
        )}

        {showOta && (
          <>
            <div>
              <SyutSelect
                label={<>OTA <span className="text-danger">*</span></>}
                isAsync
                loadOptions={loadOtaOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={data.otaId}
                onChange={((val: string | string[] | null, _: any, opt?: SelectOption | null) => {
                  onChange({ otaId: (val as string) || '', otaName: opt?.label || '' });
                  void applyPartyDefault('OTA', (val as string) || '');
                }) as any}
                placeholder="Select OTA..."
                size="md"
                error={fieldErrors['otaId']}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <label className="form-label">OTA Booking Ref <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors['otaBookingRef'] ? ' form-field-error' : ''}`}
                value={data.otaBookingRef}
                onChange={e => onChange({ otaBookingRef: e.target.value })}
                placeholder="Reference number"
                maxLength={50}
              />
              {fieldErrors['otaBookingRef'] && <div className="form-error-message">{fieldErrors['otaBookingRef']}</div>}
            </div>
          </>
        )}

        {/* Settlement (slice 3b) — only a booking with a party has a choice. */}
        <div style={{ gridColumn: '1 / -1' }} data-field="settlementMode">
          {party ? (
            <SyutSelect
              label="Settlement"
              options={settlementOptions}
              value={data.settlementMode}
              onChange={(v) => { setResetNote(null); onChange({ settlementMode: ((v as string) || FOLLOW_PARTY) as SettlementChoice }); }}
              isClearable={false}
              size="md"
              error={fieldErrors['settlementMode']}
              menuPortalTarget={document.body}
              helperText={resetNote ?? (settlementHint
                ? `${settlementHint}${partyInfo ? ` Party: ${partyInfo.name}.` : ''}`
                : undefined)}
            />
          ) : (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Settlement: <b style={{ color: 'var(--text-primary)' }}>Guest pays</b> — no agent, corporate or OTA on this booking.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceChannelSection;
